import React from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ProjectBasedLearningCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const IllDefinedCriteriaStep: React.FC<
  StepComponentProps<ProjectBasedLearningCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();

  const handleIllDefinedCriteriaChange = (value: string) => {
    onDataChange({ illDefinedCriteria: value });
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("projectBasedLearning.steps.criteria.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("projectBasedLearning.steps.criteria.description")}
        </p>
      </div>

      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("projectBasedLearning.form.illDefinedCriteria")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("projectBasedLearning.form.illDefinedCriteriaDesc")}
        </p>

        <div className="space-y-4">
          <textarea
            value={data.illDefinedCriteria || ""}
            onChange={(e) => handleIllDefinedCriteriaChange(e.target.value)}
            className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200 min-h-[150px]`}
            placeholder={t(
              "projectBasedLearning.form.illDefinedCriteriaPlaceholder",
            )}
          />
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700/50">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-800/50 text-amber-600 dark:text-amber-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                {t("projectBasedLearning.criteria.infoTitle")}
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t("projectBasedLearning.criteria.infoDescription")}
              </p>
            </div>
          </div>
        </div>

        {/* Examples */}
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
            {t("projectBasedLearning.criteria.examples")}:
          </h4>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-start gap-2">
              <span className="text-red-500">❌</span>
              <span>{t("projectBasedLearning.criteria.badExample")}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-500">✅</span>
              <span>{t("projectBasedLearning.criteria.goodExample")}</span>
            </div>
          </div>
        </div>

        {/* Guiding Questions */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
          <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-3">
            {t("projectBasedLearning.criteria.guidingQuestions")}:
          </h4>
          <ul className="space-y-2 text-sm text-blue-600 dark:text-blue-400">
            <li>• {t("projectBasedLearning.criteria.question1")}</li>
            <li>• {t("projectBasedLearning.criteria.question2")}</li>
            <li>• {t("projectBasedLearning.criteria.question3")}</li>
            <li>• {t("projectBasedLearning.criteria.question4")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
